import React, { useState, useEffect } from 'react';
import FileUpload from '../common/FileUpload';
import DataPreview from '../common/DataPreview';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../../context/NotificationContext';
import { dataProcessingService } from '../../services/api/dataProcessingService';
import { CleaningOperation, DataCleaningConfig, ProcessingResult } from '../../types/dataProcessing';
import * as XLSX from "xlsx";
import CleaningOperations from '../features/cleaning/CleaningOperations';
import DataVisualization from '../features/visualization/DataVisualization';
import VisualizationControls from '../features/visualization/VisualizationControls';

const DataCleaningPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [isProcessing, setIsProcessing] = useState(false);
  const [config, setConfig] = useState<DataCleaningConfig>({
    // protectedColumns: [],
    // excludedColumns: [],
    // formatSettings: {
    //   csv: { delimiter: ',' },
    //   excel: { headerRow: undefined }
    // }
    operations: [],
    formatSettings: {
      csv: { delimiter: ',' }
    }
  });
    const [result, setResult] = useState<ProcessingResult | undefined>(undefined);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [chartType, setChartType] = useState<'line' | 'bar'>('line');
    const [targetColumns, setTargetColumns] = useState<string[]>([]);
    const [file, setFile] = useState<File | null>(null);
//   preview data fix
    const [columns, setColumns] = useState<string[]>([]);
  
    const [selectedXAxis, setSelectedXAxis] = useState<string>('');
    const [selectedYAxis, setSelectedYAxis] = useState<string>('');
//   const [chartType, setChartType] = useState<'line' | 'bar'>('line');
//   const [statistics, setStatistics] = useState<DatasetStatistics | null>(null);
//   const [qualityResult, setQualityResult] = useState<QualityCheckResult | null>(null);
//   const [transformationRules, setTransformationRules] = useState<TransformationRule[]>([]);
//   const [validationRules, setValidationRules] = useState<ValidationRule[]>([]);
//   const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const acceptedTypes = [
    'csv',
    'excel',
  ];

  const handleConfigChange = (field: keyof DataCleaningConfig, value: any) => {

    setConfig(prev => ({ ...prev, [field]: value }));
    
  };
    const parseFile = (file: File) => {
        const reader = new FileReader();
        const fileSampleSize = 500 * 1024 * 1024; // 500MB
        const blob = file.slice(0, fileSampleSize);
        
        reader.onload = (event) => {
  
            const data = event.target?.result;
            if (!data) return;

            if (file.name.endsWith(".csv")) {
                
                const csvText = data as string;
                const sampleText = csvText.split('\n').slice(0, 10000).join('\n'); // Get first 10 lines
                parseCSV(sampleText);
            } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
                if (data instanceof ArrayBuffer) {
                    parseExcel(data, 10000);
                } else if (typeof data === "string") {
                    const sampleText = data.split('\n').slice(0, 10000).join('\n'); // Get first 10 lines
                    parseCSV(sampleText);

                }
            }
        };

        if (file.name.endsWith(".csv")) {
            
            reader.readAsText(blob);
        } else {
            reader.readAsArrayBuffer(blob);
        }
    };

    const detectDelimiter = (csvText: string): string => {
        const commonDelimiters = [',', ';', '\t', '|'];
    
        // Get the first few lines of the CSV text
        const firstLines = csvText.split('\n').slice(0, 10).join('\n');
    
        // Count occurrences of each delimiter in the first few lines
        const counts = commonDelimiters.map((delimiter) => {
            // Use a regular expression to count non-quoted delimiters
            const regex = new RegExp(`(?<!")\\${delimiter}(?!")`, 'g');
            const count = (firstLines.match(regex) || []).length;
            return { delimiter, count };
        });
    
        // Find the delimiter with the highest count
        const bestDelimiter = counts.reduce((prev, curr) =>
            curr.count > prev.count ? curr : prev
        ).delimiter;
    
        // Default to comma if no delimiter is found
        return bestDelimiter || ',';
    };
    
    const parseCSV = (csvText: string) => {
        // Detect the delimiter
        
        const delimiter = detectDelimiter(csvText);
        
    
        // Split rows and columns using the detected delimiter
        const rows = csvText
            .split('\n')
            .map((row) => row.split(delimiter).map((cell) => cell.trim()));
    
        if (rows.length < 2) return; // Ensure there's data
    
        // Extract columns from the first row
        const columns = rows[0];
    
        // Process data rows
        const data = rows.slice(1).map((row) => {
            return columns.reduce((obj, col, index) => {
                obj[col] = row[index]?.trim() || '';
                return obj;
            }, {} as Record<string, string>);
        });
    
 
    
        setTargetColumns(columns);
        setPreviewData(data);
        
        setIsPreviewLoading(false);
    };
    

    const detectHeaderRow = (jsonData: unknown[][]): number => {
        let maxScore = -1;
        let bestHeaderRow = 0;
    
        // Check the first 10 rows for the header
        for (let i = 0; i < Math.min(jsonData.length, 10); i++) {
            const row = jsonData[i];
            const rowValues = row;
    
            // Skip empty rows
            if (rowValues.length === 0) continue;
    
            let score = 0;
    
            // 1. Non-numeric values are more likely to be headers
            const nonNumericCount = rowValues.filter((val) => typeof val === "string").length;
            score += nonNumericCount * 2;
    
            // 2. Unique values are more likely to be headers
            const uniqueValues = new Set(rowValues);
            const uniquenessScore = (uniqueValues.size / rowValues.length) * 3;
            score += uniquenessScore;
    
            // 3. Check for common header keywords
            const headerKeywords = [
                "id", "name", "date", "total", "number", "code", "type", 
                "year", "revenue", "profit", "cost", "expense", "usd", "million"
            ];
            const keywordScore = rowValues.filter((val) =>
                headerKeywords.some((keyword) =>
                    String(val).toLowerCase().includes(keyword)
                )
            ).length * 2;
            score += keywordScore;
    
            // 4. Penalize rows with empty values
            const emptyValuePenalty = rowValues.filter((val) => val === null || val === undefined || val === "").length;
            score -= emptyValuePenalty * 2;
    
            // 5. Penalize rows with very long strings (unlikely to be headers)
            const longStringPenalty = rowValues.filter((val) => String(val).length > 50).length;
            score -= longStringPenalty;
    
            // 6. Check type consistency in rows below
            if (i < jsonData.length - 1) {
                const belowRows = jsonData.slice(i + 1);
                let typeConsistency = 0;
    
                belowRows.forEach((belowRow) => {
                    const belowRowValues = belowRow;
                    let matchCount = 0;
    
                    rowValues.forEach((val, idx) => {
                        if (belowRowValues[idx] !== undefined && typeof val === typeof belowRowValues[idx]) {
                            matchCount++;
                        }
                    });
    
                    typeConsistency += matchCount / rowValues.length;
                });
    
                typeConsistency /= belowRows.length;
                score += typeConsistency * 4;
            }
    
            // Update the best header row if this row has a higher score
            if (score > maxScore) {
                maxScore = score;
                bestHeaderRow = i;
            }
        }
    
        return bestHeaderRow;
    };

    const parseExcel = (data: ArrayBuffer, sampleSize: number) => {
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0]; // First sheet
        const sheet = workbook.Sheets[sheetName];

        // Get raw data including headers (each row is an array of values)
        const jsonData: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length === 0) return;

        // Detect the header row
        const headerRowIndex = detectHeaderRow(jsonData);

        // Extract the header (assert it as an array of strings)
        const header: string[] = jsonData[headerRowIndex] as string[];

        // Extract the data rows (assert each row as an array of unknown values)
        const dataRows: unknown[][] = jsonData.slice(headerRowIndex + 1, headerRowIndex + 1 + sampleSize);
        
        // Convert data rows to objects with header keys
        const processedData = dataRows.map((row) => {
            const rowObject: Record<string, unknown> = {};
            header.forEach((key, index) => {
                rowObject[key] = row[index];
            });
            return rowObject;
        });
    
        
    
        setTargetColumns(header);
        setPreviewData(processedData);
        setIsPreviewLoading(false);
    };

    const handleOperationRemove = (index: number) => {
        setConfig(prev => ({
          ...prev,
          operations: prev.operations.filter((_, i) => i !== index)
        }));
        
      };

    const handleOperationAdd = (operation: CleaningOperation) => {
        if (config.operations.length >= 10) {
          showNotification("error", "Cannot add more than 10 operations.")
          return null
          
        }
        setConfig(prev => ({
          ...prev,
          operations: [...prev.operations, operation]
        }));
        
      };


  useEffect(() => {
        if (file) {
            setPreviewData([]);
            setResult(undefined)
            parseFile(file);
            
            
        } else {
            setTargetColumns([]);
            setPreviewData([]);
        }
    }, [file]); 

  useEffect(() => {
        
        if (previewData.length > 0) {
            setColumns(Object.keys(previewData[0]));
            const dataColumns = Object.keys(previewData[0])
            setSelectedXAxis(dataColumns[0]);
            setSelectedYAxis(dataColumns[1]);
            
        } else {
            setColumns([]);
        }
    }, [previewData]); 

//   const handleColumnInput = (type: 'protected' | 'excluded', value: string) => {
//     const edit_columns = value.split(',').map(col => col.trim()).filter(Boolean);
//     console.log(edit_columns)
//     handleConfigChange(type === 'protected' ? 'protectedColumns' : 'excludedColumns', edit_columns);
//   };

  const handleSampleProcessing = async (file: File) => {
    try {
      setIsPreviewLoading(true);
     
      const sampleResponse = await dataProcessingService.processSample(file, config);
      setResult({
            success: true,
            previewData: sampleResponse.previewData,
            result: {
                downloadUrl: sampleResponse.downloadUrl, // Set the download URL here
            },
        });
       
      setPreviewData(sampleResponse.previewData);
      
      
      showNotification('success', 'Sample processing completed');
    } catch (error) {
      console.error('Sample processing error:', error);
      showNotification('error', 'Failed to process sample data');
    } finally {
      setIsPreviewLoading(false);
    }
  };

    const cleanFullData = async (file: File) => {
        try {
            setIsProcessing(true);
            const response = await dataProcessingService.uploadFile(file, config);
            setResult({
                success: true,
                previewData: response.previewData,
                result: {
                    downloadUrl: response.downloadUrl, // Set the download URL here
                },
            });
            
            setPreviewData(response.previewData)
            showNotification('success', 'Processing completed');
            
        } catch (error) {
            console.error("Error during cleaning full data:", error);
            showNotification('error', 'An error occurred during processing. Please try again.');
        } finally {
            setIsProcessing(false); // Ensure processing state is reset
        }
    };

  const handleFileSelect = async (file: File) => {
    try {
      // First process sample
      setIsPreviewLoading(true);
      setFile(file);

    //   await handleSampleProcessing(file);

      //In order to complete this must implement jobId
    //   const pollStatus = setInterval(async () => {
    //     const statusResponse = await dataProcessingService.getProcessingStatus(response.jobId);
    //     setResult(prev => ({
    //       ...prev!,
    //       status: statusResponse.status as ProcessingResult['status'],
    //       progress: statusResponse.progress
    //     }));

    //     if (statusResponse.status === 'completed') {
    //       showNotification('success', 'File processed successfully!');
    //       clearInterval(pollStatus);
    //       setIsProcessing(false);
    //     } else if (statusResponse.status === 'failed') {
    //       showNotification('error', 'Processing failed. Please try again.');
    //       clearInterval(pollStatus);
    //       setIsProcessing(false);
    //     }
    //   }, 2000);

    } catch (error) {
      console.error('Processing error:', error);
      showNotification('error', 'An error occurred while processing the file.');
      setIsProcessing(false);
    }

  };

  return (
    <div className="data-cleaning-page">
        <h1>Data Cleaning</h1>
        <p className="description">
            Upload your dataset for cleaning. Configure the processing options below.
        </p>

        

        <CleaningOperations
              operations={config.operations}
              onAdd={handleOperationAdd}
              onRemove={handleOperationRemove} columns={targetColumns}      />
            {/* left off */}
        
        <FileUpload
            acceptedTypes={acceptedTypes}
            onFileSelect={handleFileSelect}
            
        />

        {isPreviewLoading ? (
            <LoadingSpinner />
        ) : (
            
            <DataPreview data={previewData} isLoading={isPreviewLoading} columns={columns} />
            
        )}
      
        {previewData.length > 0 ? (
            
            <div className="result-section">
                {/* <h3>Processing Complete</h3> */}
                <button 
                    className="download-button"
                    onClick={() => {
                        if (file) {
                            cleanFullData(file); 
                        } else {
                            showNotification('error', 'Please select a file before proceeding.');
                        }
                    }}
                >
                    Clean Full File
                </button>
                <button 
                    className="download-button"
                    onClick={() => {
                        if (file) {
                            handleSampleProcessing(file); 
                        } else {
                            showNotification('error', 'Please select a file before proceeding.');
                        }
                    }}
                >
                    Clean Sample File 
                </button>
            </div>
            
            ) : null}

        {isProcessing && <LoadingSpinner />}
        {result && result.result?.downloadUrl && (
            <div className="result-section">
                <h3>Processing Complete</h3>
                <button 
                    className="download-button"
                    onClick={() => {
                        if (result.result?.downloadUrl) {
                            const link = document.createElement("a");
                            link.target = "_blank"; 
                            link.href = result.result.downloadUrl;
                            // link.setAttribute("download", "cleaned_data.csv"); 
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        } else {
                            console.error('Download URL is undefined');
                        }
                    }}
                >
                    Download Cleaned File 
                </button>
            </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            {previewData.length === 0 && (
                <span style={{ textAlign: 'center', width: '100%' }}>
                    Upload Your Data to View Visualizations
                </span>
            )}
        </div>

        {previewData.length > 0 && (
            <>
            <div className="visualization-section">
                <h2>Data Visualization</h2>
                <p>Currently limited to displaying up to 100 entries (for now).</p>
                <VisualizationControls
                columns={columns}
                selectedXAxis={selectedXAxis}
                selectedYAxis={selectedYAxis}
                chartType={chartType}
                onXAxisChange={setSelectedXAxis}
                onYAxisChange={setSelectedYAxis}
                onChartTypeChange={setChartType}
                />
                <DataVisualization
                data={previewData}
                type={chartType}
                xAxis={selectedXAxis}
                yAxis={selectedYAxis}
                title="Data Preview Visualization"
                />
            </div>
            {/* <ExportOptions onExport={handleExport} /> */}
            </>
        )}
    {/* only useable when jobId implemented */}
      {/* {result?.status === 'completed' && result.result?.downloadUrl && (
        <div className="result-section">
          <h3>Processing Complete</h3>
          <a 
            href={result.result.downloadUrl}
            className="download-button"
            download
          >
            Download Processed File
          </a>
        </div>
      )} */}
    </div>
  );
};

export default DataCleaningPage; 